import React, { useEffect, useState } from 'react';
import { Card, Row, Col, DatePicker, Spin, Statistic, Empty, Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  ComposedChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useTranslation } from 'react-i18next';
import operatorApiFetch from '@services/operator/apiService';
import dayjs from 'dayjs';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { HideInStandard } from '@components/operator/shared/StandardGate';

const { RangePicker } = DatePicker;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const OrderReports = () => {
  const { t } = useTranslation();
  const { merchants, merchantId, loading: merchantsLoading } = useOperatorData();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    daily_chart_data: [],
    merchant_chart_data: [],
    carrier_chart_data: [],
    summary: {}
  });
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  
  // Initialize empty; single-merchant mode populates via effect when context loads
  const [selectedMerchantIds, setSelectedMerchantIds] = useState([]);

  // 若 context 中的 merchantId 於掛載後才完成載入（single-merchant 模式鎖定），同步更新狀態
  useEffect(() => {
    if (merchantId && selectedMerchantIds.length === 0) {
      setSelectedMerchantIds([merchantId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  useEffect(() => {
    // Single-merchant 模式下，等 merchantId 載入後才發請求，避免 cold start 時的多餘 fetch
    if (merchantId && selectedMerchantIds.length === 0) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedMerchantIds, merchantId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [start, end] = dateRange;
      const params = new URLSearchParams({
        start_date: start.format('YYYY-MM-DD'),
        end_date: end.format('YYYY-MM-DD')
      });

      if (selectedMerchantIds.length > 0) {
        params.append('merchant_ids', selectedMerchantIds.join(','));
      }

      const response = await operatorApiFetch(`reports/orders_statistics?${params}`);
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      }
    } catch (error) {
      console.error('Fetch report failed', error);
    } finally {
      setLoading(false);
    }
  };

  const merchantOptions = (merchants || []).map(m => ({
    label: m.name,
    value: m.id
  }));

  const stats = [
    {
      title: t('operator.reports.orders.sla_compliance'),
      tooltip: t('operator.reports.orders.tooltips.sla_compliance'),
      value: data.summary.sla_compliance_rate,
      suffix: '%',
      color: data.summary.sla_compliance_rate >= 95 ? '#52c41a' : '#cf1322'
    },
    {
      title: t('operator.reports.orders.total_units'),
      tooltip: t('operator.reports.orders.tooltips.total_units'),
      value: data.summary.total_units,
      color: '#1890ff'
    },
    {
      title: t('operator.reports.orders.avg_lines'),
      tooltip: t('operator.reports.orders.tooltips.avg_lines'),
      value: data.summary.avg_lines_per_order,
      color: '#faad14'
    },
    {
      title: t('operator.reports.orders.processing_time'),
      tooltip: t('operator.reports.orders.tooltips.processing_time'),
      value: data.summary.avg_processing_hours,
      suffix: 'h',
      color: '#722ed1'
    },
  ];

  // Date restriction: Max today, Min 90 days ago
  const disabledDate = (current) => {
    return current && (
      current > dayjs().endOf('day') || 
      current < dayjs().subtract(90, 'days').startOf('day')
    );
  };

  return (
    <div className="page-content">
      <div className="page-header" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div className="page-title">{t('operator.menu.reports.orders')}</div>
        <div className="page-actions" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <HideInStandard>
            <div style={{ minWidth: 250, maxWidth: 400 }}>
               <Select
                  mode="multiple"
                  maxCount={10}
                  maxTagCount="responsive"
                  style={{ width: '100%' }}
                  value={selectedMerchantIds}
                  onChange={setSelectedMerchantIds}
                  allowClear
                  placeholder={t('operator.placeholder.select_merchant')}
                  options={merchantOptions}
                  loading={merchantsLoading}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
            </div>
          </HideInStandard>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            allowClear={false}
            disabledDate={disabledDate}
          />
        </div>
      </div>

      <Spin spinning={loading}>
        {/* KPI Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {stats.map((stat, index) => (
            <Col xs={12} sm={6} md={6} lg={6} key={index}>
              <Card bordered={false}>
                <Statistic
                  title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {stat.title}
                      <Tooltip title={stat.tooltip}>
                        <InfoCircleOutlined style={{ fontSize: 14, color: '#999', cursor: 'help' }} />
                      </Tooltip>
                    </span>
                  }
                  value={stat.value}
                  precision={stat.title === t('operator.reports.orders.total_units') ? 0 : 1}
                  suffix={stat.suffix}
                  valueStyle={{ color: stat.color }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts Row 1: Daily Trend (Orders vs Units) */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Card title={t('operator.reports.orders.daily_trend')} bordered={false}>
              <div style={{ width: '100%', height: 350 }}>
                <ResponsiveContainer>
                  <ComposedChart data={data.daily_chart_data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="left" label={{ value: t('operator.reports.orders.order_count'), angle: -90, position: 'insideLeft' }} />
                    <YAxis yAxisId="right" orientation="right" label={{ value: t('operator.reports.orders.unit_count'), angle: 90, position: 'insideRight' }} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="created" name={t('operator.reports.orders.created_orders')} fill="#413ea0" barSize={20} />
                    <Bar yAxisId="left" dataKey="shipped" name={t('operator.reports.orders.shipped_orders')} fill="#52c41a" barSize={20} />
                    <Line yAxisId="right" type="monotone" dataKey="units" name={t('operator.reports.orders.total_units')} stroke="#ff7300" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Charts Row 2: Merchants & Carriers */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title={t('operator.reports.orders.top_merchants')} bordered={false}>
              <div style={{ width: '100%', height: 300 }}>
                {data.merchant_chart_data.length > 0 ? (
                  <ResponsiveContainer>
                    <BarChart layout="vertical" data={data.merchant_chart_data} margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={100} />
                      <RechartsTooltip />
                      <Bar dataKey="value" name={t('operator.reports.orders.order_count')} fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <Empty />}
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title={t('operator.reports.orders.carrier_distribution')} bordered={false}>
              <div style={{ width: '100%', height: 300 }}>
                {data.carrier_chart_data.length > 0 ? (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={data.carrier_chart_data.map(entry => ({
                          ...entry,
                          name: entry.code
                            ? t(`enums.shipping_type.provider.${entry.code}`, { defaultValue: entry.name || entry.code })
                            : (entry.name || 'Unknown'),
                        }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label
                      >
                        {data.carrier_chart_data.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <Empty />}
              </div>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default OrderReports;
